import { ReportData } from '../types/reportTypes';

// Parse currency string to number
const parseCurrency = (value: string): number => {
  if (!value || value === '') return 0;
  return parseFloat(value.replace(/[$,]/g, ''));
};

// Parse percentage string to number
const parsePercentage = (value: string): number => {
  if (!value || value === '') return 0;
  return parseFloat(value.replace(/%/g, '')) / 100;
};

// Calculate the sum of an array of numbers
const sum = (numbers: number[]): number => {
  return numbers.reduce((total, num) => total + num, 0);
};

// Calculate net profit and commission for Cody Cline reports
const calculateCodyNetProfit = (grossProfit: number, companyOH: number): number => {
  // For Cody Cline: Net Profit = Gross Profit - Company OH
  return grossProfit - companyOH;
};

const calculateCodyCommission = (netProfit: number): number => {
  // For Cody Cline: Sales Commission = 50% of Net Profit
  return netProfit / 2;
};

// Calculate the individual job data from the Cody Sales January CSV
// IMPORTANT: Using 13% flat OH calculation (Contract Total × 0.13) retroactive to 1/1/2025
const jobData = [
  {
    jobInfo: "206: Naghma Husain",
    leadSource: "Referral- Cody's network",
    trade: "Roofing",
    contractTotal: 17555.00,
    grossMarginPct: 0.5201,
    grossProfit: 9130.36,
    salesCommission: 0,
    salesManagerFee: 0,
    companyOH: 17555.00 * 0.13, // 13% flat rate
    netProfit: 0
  },
  {
    jobInfo: "192: George and Linda Graefe",
    leadSource: "Referral- Cody's network",
    trade: "Windows & Doors",
    contractTotal: 5327.87,
    grossMarginPct: 0.5002,
    grossProfit: 2665.00,
    salesCommission: 0,
    salesManagerFee: 0,
    companyOH: 5327.87 * 0.13, // 13% flat rate
    netProfit: 0
  },
  {
    jobInfo: "191: Chris Meckel Lang project",
    leadSource: "Referral- Cody's network",
    trade: "Other",
    contractTotal: 19998.00,
    grossMarginPct: 0.4506,
    grossProfit: 9011.10,
    salesCommission: 0,
    salesManagerFee: 0,
    companyOH: 19998.00 * 0.13, // 13% flat rate
    netProfit: 0
  },
  {
    jobInfo: "188: Marc Masson",
    leadSource: "Previous Customer",
    trade: "Roofing",
    contractTotal: 14884.54,
    grossMarginPct: 0.4043,
    grossProfit: 6017.82,
    salesCommission: 0,
    salesManagerFee: 0,
    companyOH: 14884.54 * 0.13, // 13% flat rate
    netProfit: 0
  },
  {
    jobInfo: "204: Solution Based Property Management",
    leadSource: "Referral- Cody's network",
    trade: "Roofing",
    contractTotal: 29140.00,
    grossMarginPct: 0.4752,
    grossProfit: 13847.33,
    salesCommission: 0,
    salesManagerFee: 0,
    companyOH: 29140.00 * 0.13, // 13% flat rate
    netProfit: 0
  },
  {
    jobInfo: "199: Greenleigh Homeowners Association",
    leadSource: "Referral- Dominic's network",
    trade: "Windows & Doors",
    contractTotal: 501.05,
    grossMarginPct: 0.3000,
    grossProfit: 150.32,
    salesCommission: 0,
    salesManagerFee: 0,
    companyOH: 501.05 * 0.13, // 13% flat rate
    netProfit: 0
  },
  {
    jobInfo: "197: Laura Phillips",
    leadSource: "Referral- Cody's network",
    trade: "Roofing",
    contractTotal: 19821.63,
    grossMarginPct: 0.3262,
    grossProfit: 6465.82,
    salesCommission: 0,
    salesManagerFee: 0,
    companyOH: 19821.63 * 0.13, // 13% flat rate
    netProfit: 0
  },
  {
    jobInfo: "195: North Green Condominium Association",
    leadSource: "Referral- Dominic's network",
    trade: "Siding",
    contractTotal: 556.00,
    grossMarginPct: 0.3000,
    grossProfit: 166.80,
    salesCommission: 0,
    salesManagerFee: 0,
    companyOH: 556.00 * 0.13, // 13% flat rate
    netProfit: 0
  },
  {
    jobInfo: "147: Ellicott Hills Condo 3",
    leadSource: "Referral- Dominic's network",
    trade: "Siding",
    contractTotal: 4269.77,
    grossMarginPct: 0.3000,
    grossProfit: 1280.93,
    salesCommission: 0,
    salesManagerFee: 0,
    companyOH: 4269.77 * 0.13, // 13% flat rate
    netProfit: 0
  },
  {
    jobInfo: "205: Judith Weitz",
    leadSource: "Previous Customer",
    trade: "Siding",
    contractTotal: 16679.80,
    grossMarginPct: 0.5957,
    grossProfit: 9936.16,
    salesCommission: 0,
    salesManagerFee: 0,
    companyOH: 16679.80 * 0.13, // 13% flat rate
    netProfit: 0
  }
];

// Apply Cody's calculation formula to each job
jobData.forEach(job => {
  job.netProfit = calculateCodyNetProfit(job.grossProfit, job.companyOH);
  job.salesCommission = calculateCodyCommission(job.netProfit);
});

// Calculate sales by source - matching the format needed for charts
const salesBySource = (() => {
  const sourceMap = new Map<string, any>();
  
  jobData.forEach(job => {
    const key = job.leadSource;
    if (!sourceMap.has(key)) {
      sourceMap.set(key, {
        source: key,
        count: 0,
        totalSales: 0,
        grossProfit: 0
      });
    }
    
    const sourceData = sourceMap.get(key);
    sourceData.count += 1;
    sourceData.totalSales += job.contractTotal;
    sourceData.grossProfit += job.grossProfit;
  });
  
  return Array.from(sourceMap.values());
})();

// Create ad spend by source data - all sources have 0 spend for January
const adSpendBySource = (() => {
  const uniqueSources = new Set<string>();
  jobData.forEach(job => uniqueSources.add(job.leadSource));
  
  return Array.from(uniqueSources).map(source => ({
    source: source,
    spend: 0
  }));
})();

// Calculate sales by trade
const salesByTrade = (() => {
  const tradeMap = new Map<string, any>();
  
  jobData.forEach(job => {
    const key = job.trade;
    const normalizedKey = key === "Siding" ? "Siding" : 
                         key === "Roofing" ? "Roofing" : 
                         key === "Windows & Doors" ? "Windows & Doors" : 
                         "Other";
    
    if (!tradeMap.has(normalizedKey)) {
      tradeMap.set(normalizedKey, {
        trade: normalizedKey,
        count: 0,
        totalSales: 0,
        grossProfit: 0,
        avgContractValue: 0,
        avgGrossMargin: 0
      });
    }
    
    const tradeData = tradeMap.get(normalizedKey);
    tradeData.count += 1;
    tradeData.totalSales += job.contractTotal;
    tradeData.grossProfit += job.grossProfit;
  });
  
  // Calculate averages
  tradeMap.forEach(trade => {
    trade.avgContractValue = trade.count > 0 ? trade.totalSales / trade.count : 0;
    trade.avgGrossMargin = trade.totalSales > 0 ? trade.grossProfit / trade.totalSales : 0;
  });
  
  return Array.from(tradeMap.values());
})();

// Calculate totals
const totalJobs = jobData.length;
const totalSales = sum(jobData.map(job => job.contractTotal));
const totalGrossProfit = sum(jobData.map(job => job.grossProfit));
const totalCompanyOH = sum(jobData.map(job => job.companyOH));
const totalNetProfit = sum(jobData.map(job => job.netProfit));
const totalSalesCommissions = sum(jobData.map(job => job.salesCommission));
const totalManagerFees = 0; // No manager fees for Cody
const totalAdSpend = 0; // No ad spend for January based on CSV

// Export the full report data for January 2025
export const codyJanuaryData: ReportData = {
  period: {
    month: 1,
    year: 2025
  },
  kpis: {
    adSpend: totalAdSpend,
    totalSales,
    grossProfit: totalGrossProfit,
    salesCommissions: totalSalesCommissions,
    managerFees: totalManagerFees,
    companyOH: totalCompanyOH,
    netProfit: totalNetProfit,
    netProfitPercent: totalSales > 0 ? totalNetProfit / totalSales : 0,
    grossProfitPercent: totalSales > 0 ? totalGrossProfit / totalSales : 0
  },
  salesBySource,
  salesByTrade,
  adSpendBySource,
  jobData
};